import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import Cookies from 'js-cookie';
import React, { memo, useContext, useEffect, useState } from 'react';
import spinner from '../../../assets/icons/spinner.svg';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { PageContext } from '../../../contexts/PageContext';
import { Blok } from '../../../interfaces/Blok.interface';
import { LinkProps } from '../../../interfaces/LinkProps.interface';
import { requestQuotation } from '../../../services/quote.service';
import { setIsLoading } from '../../../store/slices/loading.store';
import { FormFieldToolTipBlokProps } from '../../FormFields/FormFieldToolTip/FormFieldToolTip';
import Heading, { HeadingTypes } from '../../Heading/Heading';
import QuoteAPIErrorList, {
  APIErrorResponse,
} from '../../QuoteForm/QuoteAPIErrors';
import QuoteForm from '../../QuoteForm/QuoteForm';
import QuoteFormHeadingBar from '../../QuoteFormHeadingBar/QuoteFormHeadingBar';
import QuoteFormTabBar from '../../QuoteFormTabBar/QuoteFormTabBar';
import TestingGrid from '../../TestingGrid/TestingGrid.molecule';
import Col from '../../Utils/Col/Col';
import Container from '../../Utils/Container/Container';
import Grid from '../../Utils/Grid/Grid';
import { pushToDataLayer } from '../../../utils/pushToDataLayer/pushToDataLayer.util';
import { getDataLayerQuoteFunnerJourney } from '../../../utils/getDataLayerQuoteFunnelJourney/getDataLayerQuoteFunnelJourney.util';
import { ProductType } from '../../../services/quote.interfaces';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import { getQueryParam } from '../../../utils/getQueryParam/getQueryParam.util';

interface QuoteStageOnePageBlokProps extends Blok {
  stage_one_tab_label: string;
  stage_two_tab_link: LinkProps[];
  stage_three_tab_link: LinkProps[];
  form_heading: string;
  form_sections: Blok[];
}

interface QuoteStageOnePageProps {
  blok: QuoteStageOnePageBlokProps;
}

interface FieldBlok extends Blok {
  key: string;
  left_column_form_field?: FieldBlok[];
  right_column_form_field?: FieldBlok[];
  form_fields?: FieldBlok[];
  validation_rule?: string;
  error_message?: string;
  input_type?: string;
  tool_tip?: FormFieldToolTipBlokProps[];
}

const getFormFields = (sections: any[]): { [key: string]: FieldBlok } => {
  const initialValues: { [key: string]: FieldBlok } = {};

  useEffect(() => {
    const leadSourceQueryParam = getQueryParam('lead_source');
    if (leadSourceQueryParam) {
      Cookies.set('lead_source_reference', leadSourceQueryParam);
    }
  }, []);

  const addFields = (fields?: FieldBlok[]) => {
    fields?.forEach((field) => {
      if (field.key) {
        initialValues[field.key] = field;
      }
      addFields(field.left_column_form_field);
      addFields(field.right_column_form_field);
    });
  };

  for (const section of sections) {
    addFields(section.form_fields);
  }

  return initialValues;
};

const formSectionsToShow = (sections: any, productType: string) => {
  return sections.filter((formSection: any) => {
    if (formSection.component !== 'conditional_form_section') return true;
    return formSection.show_if_product_is_selected === productType;
  });
};

const QuoteStageOnePage: React.FC<QuoteStageOnePageProps> = ({ blok }) => {
  const {
    stage_one_tab_label,
    stage_two_tab_link,
    stage_three_tab_link,
    form_heading,
    form_sections,
  } = blok;
  const { header, footer } = useContext(LayoutContext);
  const location = useLocation();
  const [apiErrors, setApiErrors] = useState<APIErrorResponse[]>([]);
  const [productType, setProductType] = useState<string>('buying');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setProductType(searchParams.get('type') || 'buying');
  }, [location]);

  useEffect(() => {
    pushToDataLayer({
      event: 'quote_step1_view',
      data: {
        funnel_journey: getDataLayerQuoteFunnerJourney(
          productType as ProductType
        ),
        form_type: 'full',
      },
    });
  }, []);

  const getPropertyInformation = (values: any) => {
    switch (values['conveyancy-choice']) {
      case 'buying':
        return {
          buyInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.postcode,
            isBuyingWithMortgage: values['buying-position'].includes(
              'isBuyingWithMortgage'
            ),
            isNewBuild: values['buying-position'].includes('isNewBuild'),
            isSecondOrInvestmentProperty: values['buying-position'].includes(
              'isSecondOrInvestmentProperty'
            ),
            isFirstTimeBuyer:
              values['buying-position'].includes('isFirstTimeBuyer'),
          },
        };
      case 'selling':
        return {
          sellInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.postcode,
            hasMortgageOnProperty: values['has-mortgage'].includes(
              'hasMortgageOnProperty'
            ),
          },
        };
      case 'buying-and-selling':
        return {
          buyAndSellInformation: {
            buyPostcode: values.buyPostcode,
            buyPrice: parseInt(values.buyPrice.replace(/,/g, ''), 10),
            buyTenure: values.buyTenure,
            isBuyingWithMortgage: values['buying-position'].includes(
              'isBuyingWithMortgage'
            ),
            isBuyingNewBuild: values['buying-position'].includes('isNewBuild'),
            isBuyingSecondOrInvestmentProperty: values[
              'buying-position'
            ].includes('isSecondOrInvestmentProperty'),

            sellPostcode: values.sellPostcode,
            sellPrice: parseInt(values.sellPrice.replace(/,/g, ''), 10),
            sellTenure: values.sellTenure,
            hasMortgageOnSellProperty: values[
              'hasMortgageOnSellProperty'
            ].includes('hasMortgageOnProperty'),
          },
        };
      case 'remortgaging':
        return {
          remortgageInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.postcode,
          },
        };
      default: {
        return {};
      }
    }
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);

    const requestQuotationPayload = {
      customerDetails: {
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        telephone: values.telephone,
      },
      leadSource: {
        sourceId:
          getQueryParam('lead_source') ||
          Cookies.get('lead_source_reference') ||
          '',
        previousQuotationReference:
          Cookies.get('previousQuotationReference') || '',
      },
    };

    const propertyInformation = getPropertyInformation(values);

    if (!executeRecaptcha) {
      console.log('Not available to execute recaptcha');
      return;
    }

    try {
      const gRecaptchaToken = await executeRecaptcha('quoteStageOneSubmit');

      const result = await requestQuotation({
        ...requestQuotationPayload,
        propertyInformation,
        gRecaptchaToken,
      });

      setApiErrors([]);
      setIsLoading(false);

      if (result.data?.quoteReference && result.data?.trackingRef) {
        const trackingRef =
          result.data.trackingRef || result.data.trackingReference002;
        pushToDataLayer({
          event: 'quote_step1_get_quote',
          data: {
            funnel_journey: getDataLayerQuoteFunnerJourney(
              productType as ProductType
            ),
            form_type: 'full',
          },
        });
        navigate(
          `/instant-quote/quote-stage-two/?quotation=${result.data.quoteReference}&TrackingRef=${trackingRef}`
        );
      }
    } catch (error: any) {
      setIsLoading(false);
      setApiErrors(error);
      setIsSubmitting(false);

      navigate(`/instant-quote/quote-not-found/`);
    }
  };

  const filteredSections = formSectionsToShow(form_sections, productType);

  const fields = getFormFields(filteredSections);

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired: true,
        showContactBar: false,
      }}
    >
      <div className="flex flex-col h-screen justify-between" data-theme="alt">
        {header && (
          <StoryblokComponent blok={header.content} key={header._uid} />
        )}

        <main className="mb-auto pb-20 bg-surface/background">
          <QuoteFormTabBar
            tabOne={stage_one_tab_label}
            tabTwo={stage_two_tab_link}
            tabThree={stage_three_tab_link}
            activeTabNumber={1}
          />
          <Container startingBreakpoint="sm">
            {form_heading && (
              <QuoteFormHeadingBar
                heading={form_heading}
                isBottomBorderRequired={true}
              />
            )}
          </Container>
          <Container startingBreakpoint="sm">
            <div className="flex justify-center flex-wrap">
              <div className="max-w-[33em]">
                <QuoteForm
                  sections={form_sections}
                  onSubmit={handleSubmit}
                  initValues={{ 'conveyancy-choice': productType }}
                />
                {apiErrors.length ? (
                  <div className="w-full">
                    <QuoteAPIErrorList errors={apiErrors} />
                  </div>
                ) : null}
              </div>
            </div>
          </Container>
        </main>

        {isSubmitting && (
          <div
            className={`fixed top-6 right-7 cursor-pointer w-6 h-6 z-[60] transition-all duration-500`}
          >
            <div
              className={`overflow-auto fixed h-screen w-screen z-[59] bg-[#0B212E] bg-opacity-90 top-0 left-0 cursor-pointer py-0 transition-all duration-500 backdrop-blur-sm`}
            >
              <div className="relative container mx-auto justify-center h-full w-full py-40 grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 md:col-start-4 md:col-end-10 bg-blue-500 text-center items-center flex flex-col justify-center min-h-[200px]">
                  <img src={spinner} alt="loader" className="rotate" />
                  <Heading
                    headingType={HeadingTypes.h3}
                    text="Getting your quote, just a sec..."
                    className="pt-4 !text-[white]"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {footer && (
          <StoryblokComponent blok={footer.content} key={footer._uid} />
        )}
      </div>

      <TestingGrid />
    </PageContext.Provider>
  );
};

export default memo(QuoteStageOnePage);
